import { createUserSettingApi, updateUserSettingApi } from "@/api/services/main/user-setting";
import { IconSave } from "@/assets/icons";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AccountInfoForm } from "@/features/user-setting/components/setting-form/AccountInfoForm";
import { EmailDeliveryForm } from "@/features/user-setting/components/setting-form/EmailDeliveryForm";
import { userSettingSchema } from "@/features/user-setting/schema/userSettingSchema";
import { TUserSettingForm } from "@/features/user-setting/types/UserSettingForm";
import { convertFormDataToReqDto } from "@/features/user-setting/utils/user-setting";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { isEmpty } from "@/utils/object";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { BlockerFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";

type TUserSettingEditPageProps = {
  isCreating: boolean;
  defaultValues?: Partial<TUserSettingForm>;
};
export const UserSettingEditPage: FC<TUserSettingEditPageProps> = ({ isCreating, defaultValues }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [blockerEnabled, setBlockerEnabled] = useState(true);
  const [redirect, setRedirect] = useState<string | null>(null);
  const methods = useForm<TUserSettingForm>({
    defaultValues,
    resolver: yupResolver(userSettingSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });

  const handleSave = async (data: TUserSettingForm) => {
    try {
      const payload = convertFormDataToReqDto(data);
      if (isCreating) {
        const rs = await createUserSettingApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await updateUserSettingApi(payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_SETTING.FETCH_DETAIL] });
      methods.reset(data);
      setBlockerEnabled(false);
      setRedirect(AppRoutes.userSettingDetail);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const onSubmit = async (data: TUserSettingForm) => {
    await handleSave(data);
  };

  useEffect(() => {
    if (!blockerEnabled && redirect) {
      navigate(redirect);
      setRedirect(null);
    }
  }, [blockerEnabled, redirect, navigate]);

  const shouldBlock: BlockerFunction = useMemo(
    () =>
      ({ currentLocation, nextLocation }) => {
        if (methods.formState.isSubmitted && isEmpty(methods.formState.errors)) {
          return false;
        }
        const hasDirtyFields = !isEmpty(methods.formState.dirtyFields);
        if (!hasDirtyFields) {
          return false;
        }
        const isPathnameChanged = currentLocation.pathname !== nextLocation.pathname;
        return isPathnameChanged;
      },
    [methods.formState],
  );

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={userSettingSchema} shouldBlock={shouldBlock} enableBlocker={blockerEnabled}>
      <Stack
        gap={1}
        sx={{
          minHeight: "calc(100vh - 194px)",
        }}
      >
        <TopPage
          title="ユーザー設定"
          enablePageBack={false}
        />
        <Stack gap={4} mt={2} mb={2}>
          <AccountInfoForm />
          <EmailDeliveryForm />
        </Stack>
      </Stack>

      <FooterContainer>
        <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
          <Button variant="ghost" onClick={() => navigate(AppRoutes.userSettingDetail)}>
            戻る
          </Button>
          <Stack direction="row" gap={3}>
            <Button type="submit" variant="primary" size="sm" startIcon={<IconSave />} sx={{ fontWeight: 500 }}>
              保存
            </Button>
          </Stack>
        </Stack>
      </FooterContainer>
    </Form>
  );
};
